import { useId, type HTMLAttributes, type ReactNode } from 'react';

import { cx } from '../../lib/cx';

import styles from './Progress.module.css';

export type ProgressTone = 'olive' | 'lavender' | 'soft';

const clamp = (value: number) => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0 to 100; anything outside is brought back in. */
  value: number;
  tone?: ProgressTone;
  /** What is measured. Required: a bare bar says nothing to a screen reader. */
  label: string;
}

/** A bar that shows a share of something done. */
export function Progress({ value, tone = 'olive', label, className, ...rest }: ProgressProps) {
  const percent = clamp(value);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      className={cx(styles.track, className)}
      {...rest}
    >
      <div className={cx(styles.value, styles[tone])} style={{ width: `${percent}%` }} />
    </div>
  );
}

export interface MeterProps {
  label: ReactNode;
  value: number;
  tone?: ProgressTone;
  className?: string;
}

/** A progress bar with its name and its number written out above it. */
export function Meter({ label, value, tone = 'olive', className }: MeterProps) {
  const id = useId();
  const percent = Math.round(clamp(value));
  return (
    <div className={cx(styles.meter, className)}>
      <div className={styles.row}>
        <span id={id} className={styles.name}>
          {label}
        </span>
        <span className={cx(styles.number, styles[`${tone}Text`])}>{percent}%</span>
      </div>
      <div
        role="progressbar"
        aria-labelledby={id}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className={styles.track}
      >
        <div className={cx(styles.value, styles[tone])} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
